import React, { FC, PropsWithChildren, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Col, Form, Row } from 'antd';
import classNames from 'classnames';
import { useCountingTotalEquipment } from '@core/utils/hooks';
import { SCOPING_SECTORS_VERSIONS_DATA } from '@models/constants';
import { FormProps } from '@models/interfaces';
import {
  EquipmentTotals,
  OtherEquipment,
  ScopingNotes,
} from '@shared/components';
import { SiteInformation } from '@shared/components/SOF-information/components';
import {
  EquipmentSelectors,
  PermittingSelectors,
  ProjectCategoriesSelectors,
} from '@store/selectors';
import { PermittingProjectInformation } from '../project-information';
import { PermittingSectorsOfEquipment } from '../sectors-of-equipment';

import './styles.scss';

const { useForm } = Form;

export const PermittingSOFInformation: FC<FormProps> = ({
  isEditing = false,
}: PropsWithChildren<FormProps>) => {
  const [siteForm] = useForm();
  const [projectForm] = useForm();
  const [scopingNotesForm] = useForm();

  const { project, site, scoping } = useSelector(
    PermittingSelectors.getSofTabData,
  );

  const sectorsDataVersions = useMemo(() => {
    if (scoping?.scopingSectorsVersions?.length) {
      return scoping.scopingSectorsVersions.map(scopingDataVersion => ({
        ...scopingDataVersion,
        isSelected: scopingDataVersion.isCurrent,
      }));
    }

    return SCOPING_SECTORS_VERSIONS_DATA;
  }, [scoping?.scopingSectorsVersions]);

  const equipmentTypes = useSelector(EquipmentSelectors.getEquipmentTypes);
  const projectCategoriesTableVisible = useSelector(
    ProjectCategoriesSelectors.getProjectCategoriesTableVisible,
  );

  const totalEquipments = useCountingTotalEquipment(
    'scoping',
    scoping.otherEquipments,
    sectorsDataVersions,
    equipmentTypes,
  );

  return (
    <div className="prov-scoping-equipment">
      <div
        className={classNames('tabs-wrap', {
          'tabs-wrap_with-actions': isEditing,
          'categories-table-open': projectCategoriesTableVisible,
        })}
      >
        <Row justify="space-between" className="prov-engineering-sof-tab__row">
          <Col span="12">
            <SiteInformation form={siteForm} isEditing={false} data={site} />
          </Col>
          <Col span="12">
            <PermittingProjectInformation
              form={projectForm}
              isEditing={false}
              data={project}
            />
          </Col>
        </Row>
        <ScopingNotes
          className="prov-scoping-equipment__information"
          isEditing={isEditing}
          form={scopingNotesForm}
          data={scoping}
        />
        <PermittingSectorsOfEquipment
          className="prov-scoping-equipment__information"
          isEditing={isEditing}
          sectorsDataVersions={sectorsDataVersions}
        />
        <OtherEquipment
          className="prov-scoping-equipment__information"
          isEditing={isEditing}
          data={scoping.otherEquipments}
        />
        <EquipmentTotals
          className="prov-scoping-equipment__information"
          data={totalEquipments}
        />
      </div>
    </div>
  );
};
